// src/pages/Login/Login.jsx (Completo e Atualizado)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import styles from './Login.module.css';
import logo from '../../img/logo.png';
import { FaUser, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { API_BASE_URL } from "../../config";

// --- NOVAS CONSTANTES DE CONFIGURAÇÃO ---
const MAX_LOGIN_ATTEMPTS = 5;
const LOGIN_COOLDOWN_SECONDS = 60;

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // --- NOVOS ESTADOS PARA CONTROLE DE TENTATIVAS E COOLDOWN ---
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isCooldown, setIsCooldown] = useState(false);
  const [cooldownTimeLeft, setCooldownTimeLeft] = useState(0);

  // Efeito para verificar e gerenciar o cooldown na inicialização do componente
  useEffect(() => {
    const cooldownUntil = localStorage.getItem('loginCooldownUntil');
    const attempts = parseInt(localStorage.getItem('loginAttempts') || '0', 10);
    setLoginAttempts(attempts);

    if (cooldownUntil) {
      const remainingTime = Math.ceil((new Date(cooldownUntil) - new Date()) / 1000);
      
      if (remainingTime > 0) {
        setIsCooldown(true);
        setCooldownTimeLeft(remainingTime);
      } else {
        // Limpa o localStorage se o tempo de cooldown já passou
        localStorage.removeItem('loginCooldownUntil');
        localStorage.removeItem('loginAttempts');
        setLoginAttempts(0);
      }
    }
  }, []);

  // Efeito para o cronômetro de cooldown
  useEffect(() => {
    if (isCooldown && cooldownTimeLeft > 0) {
      const timer = setInterval(() => {
        setCooldownTimeLeft(prevTime => prevTime - 1);
      }, 1000);

      // Limpa o timer quando o tempo acaba ou o componente é desmontado
      return () => clearInterval(timer);
    } else if (cooldownTimeLeft <= 0 && isCooldown) {
      // Reabilita o formulário e limpa o estado e o localStorage
      setIsCooldown(false);
      setLoginAttempts(0);
      localStorage.removeItem('loginCooldownUntil');
      localStorage.removeItem('loginAttempts');
    }
  }, [isCooldown, cooldownTimeLeft]);


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateFields = () => {
    const errors = {};
    if (!username.trim()) {
      errors.username = 'O campo de usuário é obrigatório.';
    }
    if (!password) {
      errors.password = 'O campo de senha é obrigatório.';
    }
    return errors;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);

    Swal.fire({
      title: 'Autenticando...',
      text: 'Por favor, aguarde.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/login`, { username, password });
      
      const { token, user } = response.data;
      
      Swal.close();
      setIsLoading(false);
      
      // Limpa tentativas de erro no sucesso do login
      localStorage.removeItem('loginAttempts');
      localStorage.removeItem('loginCooldownUntil');

      localStorage.setItem('token', token);
      localStorage.setItem('authenticated', 'true');
      localStorage.setItem('tipo_usuario', user.tipo_usuario);
      localStorage.setItem('lastLoginTime', new Date().toISOString());
      localStorage.setItem('username', user.username);

      if (user.foto_perfil_url) {
        localStorage.setItem('foto_perfil_url', user.foto_perfil_url);
      } else {
        localStorage.removeItem('foto_perfil_url');
      }
      
      if(onLoginSuccess) {
        onLoginSuccess();
      }
      
      navigate('/');

    } catch (err) {
      setIsLoading(false);
      
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem('loginAttempts', newAttempts.toString());

      if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
        const cooldownUntil = new Date(new Date().getTime() + LOGIN_COOLDOWN_SECONDS * 1000);
        localStorage.setItem('loginCooldownUntil', cooldownUntil.toISOString());
        setIsCooldown(true);
        setCooldownTimeLeft(LOGIN_COOLDOWN_SECONDS);

        Swal.fire({
          icon: 'error',
          iconColor: '#dc2626',
          title: 'Muitas Tentativas de Login',
          text: `Você excedeu o número de tentativas. Por favor, aguarde ${LOGIN_COOLDOWN_SECONDS} segundos.`,
          confirmButtonText: 'Entendi',
          confirmButtonColor: '#16a34a',
        });
      } else {
        const attemptsLeft = MAX_LOGIN_ATTEMPTS - newAttempts;
        Swal.fire({
          icon: 'error',
          iconColor: '#dc2626',
          title: 'Falha na Autenticação',
          text: err.response?.data?.error || `Credenciais inválidas. Você tem mais ${attemptsLeft} tentativa(s).`,
          confirmButtonText: 'Tentar Novamente',
          confirmButtonColor: '#16a34a',
        });
      }
    }
  };

  const attemptsLeft = MAX_LOGIN_ATTEMPTS - loginAttempts;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <img src={logo} alt="Logo" className={styles.logo} />
        <p className={styles.subtitle}>Entre com suas credenciais para continuar</p>
        
        <form onSubmit={handleLogin} noValidate>
          <div className={styles.inputGroup}>
            <FaUser className={styles.inputIcon} />
            <input
              type="text"
              id="username" 
              className={`${styles.input} ${validationErrors.username ? styles.inputError : ''}`}
              placeholder="Usuário"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (validationErrors.username) {
                    const newErrors = { ...validationErrors };
                    delete newErrors.username;
                    setValidationErrors(newErrors);
                }
              }}
              disabled={isLoading || isCooldown}
            />
            {/* MODIFICAÇÃO: Adicionada Label Flutuante */}
            <label htmlFor="username" className={styles.inputLabel}>Usuário</label>
            
            {validationErrors.username && <p className={styles.fieldError}>{validationErrors.username}</p>}
          </div>

          <div className={styles.inputGroup}>
            <FaLock className={styles.inputIcon} />
            <input
              type={showPassword ? 'text' : 'password'}
              id="password" 
              className={`${styles.input} ${validationErrors.password ? styles.inputError : ''}`}
              placeholder="Senha"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (validationErrors.password) {
                    const newErrors = { ...validationErrors };
                    delete newErrors.password;
                    setValidationErrors(newErrors);
                }
              }}
              disabled={isLoading || isCooldown}
            />
            {/* MODIFICAÇÃO: Adicionada Label Flutuante */}
            <label htmlFor="password" className={styles.inputLabel}>Senha</label>

            <span onClick={togglePasswordVisibility} className={styles.passwordToggle}>
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {validationErrors.password && <p className={styles.fieldError}>{validationErrors.password}</p>}
          </div>
          
          <div className={styles.feedbackContainer}>
            {isCooldown ? (
              <p className={styles.feedbackError}>
                Tente novamente em {cooldownTimeLeft} segundos...
              </p>
            ) : (
              loginAttempts > 0 && (
                <p className={styles.feedbackWarning}>
                  Você tem mais {attemptsLeft} tentativa{attemptsLeft > 1 ? 's' : ''}.
                </p>
              )
            )}
          </div>
          
          <button type="submit" className={styles.button} disabled={isLoading || isCooldown}>
            {isLoading ? 'Entrando...' : isCooldown ? 'Bloqueado' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;